import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import fileUtils from '../../../global/utils/fileUtils.js'
import {
  SUPPORTED_FORMATS_TEXT,
  MAX_FILE_SIZE_TEXT,
} from '../constants/fileConstants.js'

const MATCHING_STEPS = [50, 60, 70, 80, 85, 90, 93, 96, 98]

const FileUploadArea = ({
  files,
  dragOver,
  setDragOver,
  onFileUpload,
  onDrop,
  onRemoveFile,
  submitting,
  uploadItems,
  onSubmitFiles,
  hasDocuments,
  isMatchingFiles,
}) => {
  const validFiles = files.filter(f => f.isValidFile)
  const [matchingProgressMap, setMatchingProgressMap] = useState({})
  const [fileProgressOffsets, setFileProgressOffsets] = useState({})

  useEffect(() => {
    if (isMatchingFiles && uploadItems) {
      const offsets = {}
      Object.values(uploadItems).forEach(item => {
        if (!fileProgressOffsets[item.name]) {
          offsets[item.name] = Math.floor(Math.random() * 3)
        }
      })
      if (Object.keys(offsets).length > 0) {
        setFileProgressOffsets(prev => ({ ...prev, ...offsets }))
      }
    }
  }, [isMatchingFiles, uploadItems])

  useEffect(() => {
    if (!isMatchingFiles || !uploadItems) {
      return
    }

    const interval = setInterval(() => {
      setMatchingProgressMap(prev => {
        const updated = { ...prev }

        Object.values(uploadItems).forEach(item => {
          if (item.percent === 100) {
            const current = prev[item.name] ?? 50
            const offset = fileProgressOffsets[item.name] ?? 0

            let targetStep = MATCHING_STEPS.find(step => step > current)

            if (targetStep) {
              const stepIndex = MATCHING_STEPS.indexOf(targetStep)
              const adjustedIndex = Math.min(
                MATCHING_STEPS.length - 1,
                stepIndex + offset,
              )
              targetStep = MATCHING_STEPS[adjustedIndex]
            }

            if (targetStep && targetStep > current) {
              updated[item.name] = targetStep
            }
          }
        })

        return updated
      })
    }, 600)

    return () => clearInterval(interval)
  }, [isMatchingFiles, uploadItems, fileProgressOffsets])

  const getUploadInfo = fileName => {
    if (!uploadItems) {
      return null
    }
    return Object.values(uploadItems).find(item => item.name === fileName)
  }

  return (
    <>
      <Paper
        variant="outlined"
        onDragOver={e => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        sx={{
          mt: 3,
          p: 4,
          textAlign: 'center',
          borderStyle: 'dashed',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: dragOver ? 'action.hover' : 'transparent',
          transition: 'background-color 0.2s ease',
        }}
      >
        <CloudUploadIcon
          fontSize="large"
          color="action"
        />
        <Typography
          variant="body1"
          mt={2}
        >
          Drop Files Here
        </Typography>
        {hasDocuments && (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Don't worry, we'll automatically match them to request list
          </Typography>
        )}
        <Button
          variant="outlined"
          component="label"
          sx={{ mt: 2, maxWidth: 150 }}
        >
          Select file
          <input
            hidden
            type="file"
            multiple
            onChange={onFileUpload}
          />
        </Button>
      </Paper>

      {files.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
          >
            Selected Files ({validFiles.length} valid)
          </Typography>
          {files.map((file, index) => {
            const uploadInfo = getUploadInfo(file.name)

            let displayPercent = 0
            let statusText = ''

            if (uploadInfo) {
              if (uploadInfo.failedMessages?.length) {
                displayPercent = uploadInfo.percent * 0.5
                statusText = uploadInfo.failedMessages[0]
              } else if (uploadInfo.percent < 100) {
                displayPercent = uploadInfo.percent * 0.5
                statusText = `Uploading ${uploadInfo.percent}%`
              } else if (isMatchingFiles) {
                const matchPercent = matchingProgressMap[file.name] ?? 50
                displayPercent = matchPercent
                statusText = `Matching ${matchPercent}%`
              } else {
                displayPercent = 50
                statusText = 'Upload complete'
              }
            }

            return (
              <Box
                key={file._id}
                sx={{
                  display: 'inline-block',
                  m: 0.5,
                  verticalAlign: 'top',
                }}
              >
                <Chip
                  label={`${file.name} (${fileUtils.formatFileSize(file.size)})`}
                  onDelete={() => onRemoveFile(index)}
                  deleteIcon={<DeleteIcon />}
                  disabled={submitting || isMatchingFiles}
                  color={file.isValidFile ? 'primary' : 'error'}
                  variant="outlined"
                />
                {uploadInfo && (
                  <>
                    <Box
                      sx={{
                        width: '100%',
                        height: 3,
                        bgcolor: 'grey.300',
                        borderRadius: 1,
                        mt: 0.5,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${displayPercent}%`,
                          height: '100%',
                          bgcolor: uploadInfo.failedMessages
                            ? 'error.main'
                            : 'primary.main',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        fontSize: '0.65rem',
                        color: 'text.primary',
                      }}
                    >
                      {statusText}
                    </Typography>
                  </>
                )}
              </Box>
            )
          })}
          {(submitting || isMatchingFiles) && (
            <Paper
              variant="outlined"
              sx={{
                bgcolor: 'blue.50',
                borderColor: 'blue.200',
                borderRadius: 2,
                p: 2,
                my: 2,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
              >
                <CircularProgress
                  size={20}
                  thickness={5}
                  sx={{ color: 'blue.500', mr: 2 }}
                />
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="medium"
                    color="blue.900"
                  >
                    Processing Files
                  </Typography>
                  <Typography
                    variant="body2"
                    color="blue.700"
                  >
                    {submitting
                      ? 'Your files are being uploaded...'
                      : isMatchingFiles
                        ? 'Your files are being matched to documents...'
                        : ''}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="caption"
          display="block"
          mt={2}
          color="text.secondary"
        >
          Supported format: {SUPPORTED_FORMATS_TEXT}
        </Typography>
        <Typography
          variant="caption"
          display="block"
          mt={2}
          color="text.secondary"
        >
          Maximum file size: {MAX_FILE_SIZE_TEXT}
        </Typography>
      </Box>

      <Box
        mt={3}
        textAlign="right"
      >
        <Button
          variant="contained"
          color="primary"
          disabled={submitting || validFiles.length === 0 || isMatchingFiles}
          onClick={onSubmitFiles}
        >
          {submitting ? 'Uploading...' : 'Submit Files'}
        </Button>
      </Box>
    </>
  )
}

export default FileUploadArea
